import { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchPlacements } from 'core/actions/ocapi';
import { usePrevious } from 'core/hooks/usePrevious';
import { selectFavoriteMerchantsFavorited } from 'core/selectors/favorites';
import { RECOMMENDED, EARN_RATE } from 'core/modules/DynamicMerchants/utils/sortTypes';
import {
  makeSelectDynamicMerchantsPlacements,
  makeSelectDynamicMerchantsIsLoading,
  makeSelectDynamicMerchantsIsLoaded,
  makeSelectDynamicMerchantsNumberOnInitialLoad,
  selectDynamicMerchantsConfig,
} from '../selectors';

export const useGetDynamicMerchants = (namespace, content_group_id, activeSort, activeFilter) => {
  const dispatch = useDispatch();
  const isInitialLoad = useRef(true);

  const selectDynamicMerchantsPlacements = useMemo(makeSelectDynamicMerchantsPlacements, []);
  const selectDynamicMerchantsIsLoading = useMemo(makeSelectDynamicMerchantsIsLoading, []);
  const selectDynamicMerchantsIsLoaded = useMemo(makeSelectDynamicMerchantsIsLoaded, []);
  const selectDynamicMerchantsNumberOnInitialLoad = useMemo(makeSelectDynamicMerchantsNumberOnInitialLoad, []);

  const config = useSelector(selectDynamicMerchantsConfig);
  const merchants = useSelector(state => selectDynamicMerchantsPlacements(state, namespace));
  const isLoading = useSelector(state => selectDynamicMerchantsIsLoading(state, namespace));
  const isLoaded = useSelector(state => selectDynamicMerchantsIsLoaded(state, namespace));
  const merchantsNumberOnInitialLoad =
    useSelector(state => selectDynamicMerchantsNumberOnInitialLoad(state, namespace));
  const favoritedMerchantIds = useSelector(selectFavoriteMerchantsFavorited);

  const prevActiveSort = usePrevious(activeSort);

  useEffect(() => {
    if ((!isLoaded && !isLoading) || activeSort !== prevActiveSort) {
      const { api: { params, options } } = config;
      const apiParams = {
        ...params,
        sort_by: activeSort,
        sort_type: activeSort === EARN_RATE ? 'desc' : params.sort_type,
        content_group_id,
      };

      dispatch(fetchPlacements(apiParams, { ...options, namespace, isInitialLoad: isInitialLoad.current }));
      isInitialLoad.current = false;
    }
  }, [activeSort]);

  const merchantsToShow = handleMerchantsToShow(merchants, favoritedMerchantIds, activeSort, activeFilter);

  return {
    merchants,
    merchantsToShow,
    merchantsNumberOnInitialLoad,
  };
};

function handleMerchantsToShow(merchants, favoritedMerchantIds, activeSort, activeFilter) {
  const filterMerchants = (sortedMerchants, activeFilter) => {
    if (!activeFilter || activeFilter === 'default' || activeFilter === 'all') {
      return sortedMerchants;
    }

    return sortedMerchants.filter(({ merchant }) => {
      const categoryIds = merchant.categories.map(({ id }) => id);
      return categoryIds.includes(Number(activeFilter));
    });
  };

  const sortFavoritedMerchants = () => {
    if (activeSort !== RECOMMENDED) {
      return merchants;
    }

    // For `ranking` sorting show favorited merchants at the top of the list
    const favoritedMerchants = [];
    const nonFavoritedMerchants = [];

    merchants.forEach(merchant => {
      if (merchant.merchant) {
        const id = String(merchant.merchant.id);
        favoritedMerchantIds.includes(id) ? favoritedMerchants.push(merchant) : nonFavoritedMerchants.push(merchant);
      }
    });

    return [...favoritedMerchants, ...nonFavoritedMerchants];
  };

  const sortedMerchants = sortFavoritedMerchants(merchants, favoritedMerchantIds, activeSort);
  const merchantsToShow = filterMerchants(sortedMerchants, activeFilter);

  return merchantsToShow;
}
